import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import MagneticButton from "./ui/MagneticButton";

const LINKS = [
  { href: "#features", label: "Recursos" },
  { href: "#tour", label: "Tour" },
  { href: "#admin", label: "Gestão" },
  { href: "#pricing", label: "Planos" },
  { href: "#faq", label: "Dúvidas" },
];

const DEMO_URL = process.env.REACT_APP_APP_URL || "https://demo.pawsuite.com.br";

/**
 * `solid` força o fundo opaco desde o topo — necessário nas páginas internas,
 * onde a barra transparente ficaria escura sobre escuro.
 */
const Navigation = ({ solid = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const { pathname } = useLocation();

  // Fora da home, as âncoras precisam voltar para "/" antes de rolar
  const isHome = pathname === "/";
  const hrefFor = (hash) => (isHome ? hash : `/${hash}`);

  // Encolhe a barra assim que a página sai do topo
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Destaca o link da seção visível
  useEffect(() => {
    const sections = LINKS.map((l) => document.querySelector(l.href)).filter(
      Boolean
    );
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(`#${visible.target.id}`);
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Trava o scroll do body com o menu mobile aberto
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <nav
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled || solid
            ? "border-b border-midnight/[.07] bg-softIvory/80 backdrop-blur-xl shadow-[0_1px_30px_-12px_rgba(15,31,61,.25)]"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between px-6 transition-all duration-500 ${
            scrolled ? "h-16" : "h-20"
          }`}
        >
          <a
            href={isHome ? "#top" : "/"}
            className="group flex items-center gap-2.5"
            data-purpose="logo"
          >
            <span className="relative">
              <span className="absolute inset-0 rounded-xl bg-tealVitality/25 blur-md transition-opacity duration-500 group-hover:opacity-100 opacity-0" />
              <img
                src="/logo.png"
                alt="PawSuite"
                className={`relative w-auto object-contain transition-all duration-500 group-hover:rotate-[-8deg] group-hover:scale-110 ${
                  scrolled ? "h-8" : "h-10"
                }`}
              />
            </span>
            <span className="font-display text-2xl font-extrabold tracking-tight text-midnight">
              PawSuite
              <span className="text-pawOrange">.</span>
            </span>
          </a>

          {/* Desktop */}
          <div className="hidden items-center gap-1 md:flex">
            {LINKS.map((link) => {
              const isActive = active === link.href;
              return (
                <a
                  key={link.href}
                  href={hrefFor(link.href)}
                  className={`relative rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
                    isActive
                      ? "text-midnight"
                      : "text-midnight/55 hover:text-midnight"
                  }`}
                >
                  {isActive && (
                    <span className="absolute inset-0 rounded-full bg-midnight/[.06]" />
                  )}
                  <span className="relative">{link.label}</span>
                  <span
                    className={`absolute -bottom-0.5 left-1/2 h-[3px] -translate-x-1/2 rounded-full bg-gradient-to-r from-tealVitality to-pawOrange transition-all duration-300 ${
                      isActive ? "w-5 opacity-100" : "w-0 opacity-0"
                    }`}
                  />
                </a>
              );
            })}

            <MagneticButton
              href={DEMO_URL}
              className="group ml-3 rounded-full bg-midnight px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-midnight/20 transition-shadow duration-300 hover:shadow-xl hover:shadow-midnight/30"
            >
              Ver Demo
              <svg
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 7l5 5-5 5M6 12h12"
                />
              </svg>
            </MagneticButton>
          </div>

          {/* Botão mobile */}
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            aria-expanded={open}
            className="relative z-50 flex h-11 w-11 items-center justify-center rounded-xl bg-midnight/5 md:hidden"
          >
            <span className="relative block h-4 w-5">
              <span
                className={`absolute left-0 block h-[2px] w-full rounded bg-midnight transition-all duration-300 ${
                  open ? "top-1/2 -translate-y-1/2 rotate-45" : "top-0"
                }`}
              />
              <span
                className={`absolute left-0 top-1/2 block h-[2px] w-full -translate-y-1/2 rounded bg-midnight transition-all duration-300 ${
                  open ? "scale-x-0 opacity-0" : ""
                }`}
              />
              <span
                className={`absolute left-0 block h-[2px] w-full rounded bg-midnight transition-all duration-300 ${
                  open ? "bottom-1/2 translate-y-1/2 -rotate-45" : "bottom-0"
                }`}
              />
            </span>
          </button>
        </div>
      </nav>

      {/* Painel mobile */}
      <div
        className={`fixed inset-0 z-40 md:hidden ${
          open ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        <div
          onClick={() => setOpen(false)}
          className={`absolute inset-0 bg-midnight/40 backdrop-blur-sm transition-opacity duration-300 ${
            open ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          className={`absolute inset-x-0 top-0 origin-top bg-softIvory px-6 pb-10 pt-24 shadow-2xl transition-all duration-500 ${
            open
              ? "translate-y-0 opacity-100"
              : "-translate-y-6 opacity-0"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(.16,1,.3,1)" }}
        >
          <div className="flex flex-col">
            {LINKS.map((link, i) => (
              <a
                key={link.href}
                href={hrefFor(link.href)}
                onClick={() => setOpen(false)}
                className="group flex items-center justify-between border-b border-midnight/[.07] py-4 font-display text-2xl font-bold text-midnight transition-all duration-500"
                style={{
                  transitionDelay: open ? `${80 + i * 55}ms` : "0ms",
                  opacity: open ? 1 : 0,
                  transform: open ? "translateY(0)" : "translateY(14px)",
                }}
              >
                {link.label}
                <svg
                  className="h-5 w-5 text-midnight/25 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-tealVitality"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 7l5 5-5 5"
                  />
                </svg>
              </a>
            ))}
          </div>

          <a
            href={DEMO_URL}
            onClick={() => setOpen(false)}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-2xl bg-midnight py-4 font-bold text-white transition-all duration-500"
            style={{
              transitionDelay: open ? `${80 + LINKS.length * 55}ms` : "0ms",
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0)" : "translateY(14px)",
            }}
          >
            Ver Demo
          </a>
        </div>
      </div>
    </>
  );
};

export default Navigation;
